import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { Json } from "@/integrations/supabase/types";

export type TranslationValue = Record<string, Record<string, string | string[]>>;

export interface TranslationField {
  locale: "zh" | "it";
  key: string;
  label: string;
  /** "lines" edits a string array, one item per line */
  type: "text" | "textarea" | "lines";
}

const asTranslations = (value: unknown): TranslationValue =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as TranslationValue) : {};

/** Drops empty strings/lists so untranslated fields fall back to English. */
export const cleanTranslations = (value: unknown): Json => {
  const out: TranslationValue = {};
  Object.entries(asTranslations(value)).forEach(([locale, fields]) => {
    Object.entries(fields || {}).forEach(([key, v]) => {
      const keep = Array.isArray(v) ? v.map((x) => x.trim()).filter(Boolean) : typeof v === "string" ? v.trim() : "";
      if ((Array.isArray(keep) && keep.length) || (typeof keep === "string" && keep)) {
        out[locale] = { ...(out[locale] || {}), [key]: keep };
      }
    });
  });
  return out as Json;
};

/** Editor for the per-locale `translations` JSON column. Empty = show English. */
export const TranslationFields = ({
  fields,
  value,
  onChange,
}: {
  fields: TranslationField[];
  value: unknown;
  onChange: (next: TranslationValue) => void;
}) => {
  const current = asTranslations(value);
  const set = (locale: string, key: string, v: string | string[]) =>
    onChange({ ...current, [locale]: { ...(current[locale] || {}), [key]: v } });

  return (
    <div className="space-y-4 pt-4 border-t border-border">
      <p className="text-xs text-muted-foreground">
        Translations (optional). Leave empty to show the English original. Chinese names/titles use the “CN” field above.
      </p>
      {fields.map((f) => {
        const id = `tr-${f.locale}-${f.key}`;
        const raw = current[f.locale]?.[f.key];
        const text = Array.isArray(raw) ? raw.join("\n") : raw || "";
        return (
          <div key={id} className="space-y-2">
            <Label htmlFor={id} className="font-mono text-xs uppercase">
              {f.label} ({f.locale.toUpperCase()})
            </Label>
            {f.type === "text" ? (
              <Input id={id} value={text} onChange={(e) => set(f.locale, f.key, e.target.value)} />
            ) : (
              <Textarea
                id={id}
                rows={f.type === "lines" ? 4 : 3}
                value={text}
                placeholder={f.type === "lines" ? "One item per line" : undefined}
                onChange={(e) => set(f.locale, f.key, f.type === "lines" ? e.target.value.split("\n") : e.target.value)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
